import React, { useCallback, useEffect, useMemo, useState } from "react";
import "../fonts/breuer-headline.css";
import "./rules.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setBackButtonUrl } from "../uiSlice";
import { fireStageHelpSwal } from "../../functions/stageRules";
import {
  STATIC_ENDLESS_RULES,
  STATIC_QUIZ_RACE_RULES,
} from "../mode/mode";
import {
  GAME_MODE_STORAGE_KEY,
  getPathAfterMode,
  getPlayableModes,
  isThemeFlagOn,
} from "../gameSetupStorage";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchPlayerModeProgress } from "../../functions/stageReportSync";
import RaceTitleBanner from "../components/RaceTitleBanner";

function readStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

const MODE_DEFS = [
  {
    id: "endless_runner",
    themeKey: "endless_runner",
    line1: "ENDLESS",
    line2: "RUNNER",
    accent: "cyan",
    infoTitle: "Endless Runner",
    fallbackRules: STATIC_ENDLESS_RULES,
  },
  {
    id: "quiz_runner",
    themeKey: "quiz_runner",
    line1: "QUIZ",
    line2: "RACE",
    accent: "magenta",
    infoTitle: "Quiz Race",
    fallbackRules: STATIC_QUIZ_RACE_RULES,
  },
];

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );
  const { status, user } = useSelector((state) => state.auth);
  const [startingMode, setStartingMode] = useState(null);
  const adminToken = useSelector(selectAdminToken);
  // DB progress: quiz played (gameover) + endless runs used (currentQues).
  const [progress, setProgress] = useState(null);
  const [progressLoaded, setProgressLoaded] = useState(false);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [status, user, dispatch]);

  const enabledModes = useMemo(() => {
    return MODE_DEFS.filter((mode) =>
      isThemeFlagOn(themeData?.[mode.themeKey], true)
    );
  }, [themeData]);

  useEffect(() => {
    if (!themeData || enabledModes.length !== 1) return;
    navigate(getPathAfterMode(enabledModes[0].id, themeData), { replace: true });
  }, [themeData, enabledModes, navigate]);

  useEffect(() => {
    let cancelled = false;
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
      /\/+$/,
      ""
    );
    fetchPlayerModeProgress({
      backendBase,
      adminToken,
      storedUser: readStoredUser(),
    }).then((p) => {
      if (cancelled) return;
      setProgress(p);
      setProgressLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [adminToken]);

  // Unknown progress (demo / fetch failed) → leave every mode open; Stage 1 re-checks the DB.
  const playableModes = useMemo(
    () =>
      progress && themeData
        ? getPlayableModes(themeData, progress)
        : enabledModes.map((m) => m.id),
    [progress, themeData, enabledModes]
  );

  useEffect(() => {
    if (!progressLoaded || !progress || enabledModes.length < 2) return;
    if (playableModes.length === 0) navigate("/leaderboard", { replace: true });
  }, [progressLoaded, progress, enabledModes, playableModes, navigate]);

  const startMode = useCallback(
    (modeId) => {
      if (startingMode || !playableModes.includes(modeId)) return;
      setStartingMode(modeId);
      try {
        navigate(getPathAfterMode(modeId, themeData), { replace: true });
      } catch (err) {
        console.error("mode select:", err);
        setStartingMode(null);
      }
    },
    [startingMode, navigate, themeData, playableModes]
  );

  const showModeInfo = useCallback(
    (mode, e) => {
      e.stopPropagation();
      e.preventDefault();
      fireStageHelpSwal({
        themeData,
        rules: mode.fallbackRules,
        title: mode.infoTitle,
      });
    },
    [themeData]
  );

  if (
    (themeStatus === "loading" && !themeData) ||
    enabledModes.length === 1 ||
    !progressLoaded
  ) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="mode-select-page">
      <RaceTitleBanner>CHOOSE YOUR MODE</RaceTitleBanner>

      {enabledModes.length === 0 ? (
        <p className="mode-select-empty">
          No game modes are enabled. Ask an admin to turn on Endless Runner or Quiz Race.
        </p>
      ) : (
        <div
          className={`mode-select-cards${
            enabledModes.length === 1 ? " mode-select-cards--single" : ""
          }`}
        >
          {enabledModes.map((mode) => {
            const done = !playableModes.includes(mode.id);
            return (
            <div
              key={mode.id}
              className={`mode-select-card mode-select-card--${mode.accent}${
                done ? " mode-select-card--done" : ""
              }`}
            >
              <button
                type="button"
                className="mode-select-card__info"
                aria-label={`${mode.infoTitle} info`}
                onClick={(e) => showModeInfo(mode, e)}
                disabled={Boolean(startingMode)}
              >
                i
              </button>
              <button
                type="button"
                className="mode-select-card__play"
                onClick={() => startMode(mode.id)}
                disabled={Boolean(startingMode) || done}
                aria-label={
                  done
                    ? `${mode.infoTitle} already played`
                    : `Play ${mode.infoTitle}`
                }
              >
                <span className="mode-select-card__label">
                  <span className="mode-select-card__line1">{mode.line1}</span>
                  <span className="mode-select-card__line2">{mode.line2}</span>
                </span>
                {startingMode === mode.id ? (
                  <span className="mode-select-card__loading">Loading…</span>
                ) : done ? (
                  <span className="mode-select-card__loading">
                    {mode.id === "quiz_runner" ? "Already played" : "No plays left"}
                  </span>
                ) : null}
              </button>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default UserRules;
export { GAME_MODE_STORAGE_KEY };
